import { c as _c } from "react/compiler-runtime";
import { noop } from 'betterplace-utils';
import { type CSSProperties, type ForwardedRef, UIEvent, forwardRef, memo, useCallback, useLayoutEffect, useState, useSyncExternalStore } from 'react';
import { useForwardedRef, useIsMounted } from '../../hooks';
import type { VirtualScrollProps, VirtualiserRenderProps } from './types';
import createVirtualiserStore from './Virtualiser';
export function VirtualScrollSSRFriendly(props) {
  const $ = _c(2);
  const mounted = useIsMounted();
  if (!mounted) {
    return null;
  }
  let t0;
  if ($[0] !== props) {
    t0 = <VirtualScroll {...props} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
function propsHaveChanged(prevProps: Record<string, unknown> | undefined, props: Record<string, unknown> | undefined) {
  if (prevProps === props) return false;
  if (!prevProps && props) return true;
  if (!props && prevProps) return true;
  return JSON.stringify(props) !== JSON.stringify(prevProps);
}
/**
 * Stabilizes the props of the first row to avoid unnecessary re-renders
 * Check if any of the props have changed and if so, update the state
 * @param props - The props to stabilize
 * @returns The stabilized props
 */
function useStabilizedProps(props) {
  const [prevProps, setPrevProps] = useState(props);
  if (propsHaveChanged(prevProps, props)) {
    setPrevProps(props);
  }
  return prevProps;
}
export function useVirtualiser(props, forwardedRef) {
  const $ = _c(12);
  const ref = useForwardedRef(forwardedRef);
  const [t0] = useState(createVirtualiserStore);
  const {
    onScroll: onScroll_,
    subscribe,
    getSnapshot,
    onRender
  } = t0;
  let t1;
  if ($[0] !== onRender || $[1] !== props || $[2] !== ref) {
    t1 = () => {
      onRender({
        ...props,
        container: ref.current
      });
    };
    $[0] = onRender;
    $[1] = props;
    $[2] = ref;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  useLayoutEffect(t1);
  const {
    items,
    totalHeight,
    containerHeight
  } = useSyncExternalStore(subscribe, getSnapshot);
  let t2;
  if ($[4] !== onScroll_) {
    t2 = event => {
      onScroll_((event.target as HTMLDivElement).scrollTop);
    };
    $[4] = onScroll_;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const onScroll = t2;
  let t3;
  if ($[6] !== containerHeight || $[7] !== items || $[8] !== onScroll || $[9] !== ref || $[10] !== totalHeight) {
    t3 = {
      ref,
      items,
      totalHeight,
      containerHeight,
      onScroll
    };
    $[6] = containerHeight;
    $[7] = items;
    $[8] = onScroll;
    $[9] = ref;
    $[10] = totalHeight;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
}
const VirtualScroll = forwardRef(function VirtualScroll(t0, fwdRef) {
  const $ = _c(32);
  let Row;
  let className;
  let props;
  let reactiveProps;
  let style;
  if ($[0] !== t0) {
    ({
      className,
      style,
      children: Row,
      reactiveProps,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = Row;
    $[2] = className;
    $[3] = props;
    $[4] = reactiveProps;
    $[5] = style;
  } else {
    Row = $[1];
    className = $[2];
    props = $[3];
    reactiveProps = $[4];
    style = $[5];
  }
  const stabilizedProps = useStabilizedProps(reactiveProps);
  const {
    ref,
    items,
    onScroll,
    totalHeight,
    containerHeight
  } = useVirtualiser(props, fwdRef);
  let t1;
  if ($[6] !== containerHeight || $[7] !== style) {
    t1 = {
      height: containerHeight,
      ...style,
      overflowY: "auto",
      position: "relative",
      scrollBehavior: "smooth",
      willChange: "contents",
      maxHeight: containerHeight
    };
    $[6] = containerHeight;
    $[7] = style;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  let t2;
  if ($[9] !== totalHeight) {
    t2 = {
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      height: totalHeight
    };
    $[9] = totalHeight;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      opacity: "0",
      pointerEvents: "none",
      position: "absolute",
      insetInline: 0
    };
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== Row || $[13] !== stabilizedProps) {
    t4 = <Row {...stabilizedProps} index={-1} key={-1} style={t3} ref={noop} />;
    $[12] = Row;
    $[13] = stabilizedProps;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== Row || $[16] !== items || $[17] !== stabilizedProps) {
    let t6;
    if ($[19] !== Row || $[20] !== stabilizedProps) {
      t6 = args => <Row {...stabilizedProps} {...args} style={{
        ...(stabilizedProps?.style as CSSProperties),
        ...(args.style as CSSProperties)
      }} key={args.index} ref={args.ref} />;
      $[19] = Row;
      $[20] = stabilizedProps;
      $[21] = t6;
    } else {
      t6 = $[21];
    }
    t5 = items.map(t6);
    $[15] = Row;
    $[16] = items;
    $[17] = stabilizedProps;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  let t6;
  if ($[22] !== t2 || $[23] !== t4 || $[24] !== t5) {
    t6 = <div style={t2}>{t4}{t5}</div>;
    $[22] = t2;
    $[23] = t4;
    $[24] = t5;
    $[25] = t6;
  } else {
    t6 = $[25];
  }
  let t7;
  if ($[26] !== className || $[27] !== onScroll || $[28] !== ref || $[29] !== t1 || $[30] !== t6) {
    t7 = <><div className={className} style={t1} ref={ref} onScroll={onScroll} data-testid="scroll-container">{t6}</div></>;
    $[26] = className;
    $[27] = onScroll;
    $[28] = ref;
    $[29] = t1;
    $[30] = t6;
    $[31] = t7;
  } else {
    t7 = $[31];
  }
  return t7;
});
export default memo(VirtualScroll);